import { useState } from 'react'
import { X, SlidersHorizontal, ChevronDown, ChevronUp, Check } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { LibraryTaxonomies } from '@/services/libraryService'

const DOCUMENT_TYPES = [
  { value: 'artigo', label: 'Artigo Científico' },
  { value: 'manual', label: 'Manual Técnico' },
  { value: 'boletim', label: 'Boletim' },
  { value: 'relatorio', label: 'Relatório' },
  { value: 'tese', label: 'Tese/Dissertação' },
  { value: 'guia', label: 'Guia Prático' },
]

interface LibraryFiltersProps {
  taxonomies: LibraryTaxonomies | null
  selectedTaxonomyIds: string[]
  selectedDocumentTypes: string[]
  onTaxonomyToggle: (id: string) => void
  onDocumentTypeToggle: (type: string) => void
  onClearAll: () => void
  hasActiveFilters: boolean
}

export function LibraryFilters({
  taxonomies,
  selectedTaxonomyIds,
  selectedDocumentTypes,
  onTaxonomyToggle,
  onDocumentTypeToggle,
  onClearAll,
  hasActiveFilters,
}: LibraryFiltersProps) {
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    categories: true,
    types: true,
    tags: false,
  })

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const activeCount = selectedTaxonomyIds.length + selectedDocumentTypes.length

  const filterContent = (
    <div className="space-y-5">
      {/* Clear all */}
      {hasActiveFilters && (
        <button
          onClick={() => { onClearAll(); setShowMobileFilters(false) }}
          className="flex items-center gap-1.5 text-xs text-brand-primary hover:underline"
        >
          <X className="w-3 h-3" />
          Limpar filtros ({activeCount})
        </button>
      )}

      {/* Categories */}
      {taxonomies && taxonomies.categories.length > 0 && (
        <div>
          <button
            onClick={() => toggleSection('categories')}
            className="flex items-center justify-between w-full text-sm font-semibold text-brand-text mb-2"
          >
            Categorias
            {expandedSections.categories ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedSections.categories && (
            <div className="space-y-1">
              {taxonomies.categories.map(cat => {
                const isSelected = selectedTaxonomyIds.includes(cat.id)
                return (
                  <button
                    key={cat.id}
                    onClick={() => onTaxonomyToggle(cat.id)}
                    className={cn(
                      "flex items-center gap-2 w-full text-left px-3 py-2 text-sm rounded-lg transition-colors",
                      isSelected
                        ? "bg-brand-primary text-white font-medium"
                        : "text-brand-text-secondary hover:bg-brand-surface-alt"
                    )}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                    {cat.name}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Document Types */}
      <div>
        <button
          onClick={() => toggleSection('types')}
          className="flex items-center justify-between w-full text-sm font-semibold text-brand-text mb-2"
        >
          Tipo de Documento
          {expandedSections.types ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {expandedSections.types && (
          <div className="space-y-1">
            {DOCUMENT_TYPES.map(dt => {
              const isSelected = selectedDocumentTypes.includes(dt.value)
              return (
                <button
                  key={dt.value}
                  onClick={() => onDocumentTypeToggle(dt.value)}
                  className={cn(
                    "flex items-center gap-2 w-full text-left px-3 py-2 text-sm rounded-lg transition-colors",
                    isSelected
                      ? "bg-brand-primary text-white font-medium"
                      : "text-brand-text-secondary hover:bg-brand-surface-alt"
                  )}
                >
                  {isSelected && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                  {dt.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Tags (Cultures + Biomes) */}
      {taxonomies && taxonomies.tags.length > 0 && (
        <div>
          <button
            onClick={() => toggleSection('tags')}
            className="flex items-center justify-between w-full text-sm font-semibold text-brand-text mb-2"
          >
            Cultura / Bioma
            {expandedSections.tags ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedSections.tags && (
            <div className="flex flex-wrap gap-1.5">
              {taxonomies.tags.map(tag => {
                const isSelected = selectedTaxonomyIds.includes(tag.id)
                return (
                  <button
                    key={tag.id}
                    onClick={() => onTaxonomyToggle(tag.id)}
                    className={cn(
                      "px-2.5 py-1.5 text-xs rounded-full transition-colors font-medium flex items-center gap-1",
                      isSelected
                        ? "bg-brand-primary text-white"
                        : "bg-brand-surface-alt text-brand-text-secondary hover:bg-brand-primary-light hover:text-brand-primary"
                    )}
                  >
                    {isSelected && <Check className="w-3 h-3" />}
                    {tag.name}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )

  return (
    <>
      {/* Mobile Filter Button */}
      <div className="lg:hidden mb-4">
        <button
          onClick={() => setShowMobileFilters(true)}
          className={cn(
            "flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors w-full justify-center",
            hasActiveFilters
              ? "bg-brand-primary text-white"
              : "bg-brand-surface text-brand-text-secondary border border-brand-border"
          )}
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filtros
          {hasActiveFilters && (
            <span className="bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">{activeCount}</span>
          )}
        </button>
      </div>

      {/* Mobile Filter Sheet */}
      {showMobileFilters && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowMobileFilters(false)}
          />
          <div className="lg:hidden fixed inset-y-0 right-0 w-80 max-w-[85vw] bg-brand-surface z-50 shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-brand-border">
              <h3 className="text-lg font-semibold text-brand-text">Filtros</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="p-2 text-brand-text-muted hover:text-brand-text"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {filterContent}
            </div>
          </div>
        </>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 flex-shrink-0">
        <div className="card p-4 sticky top-4">
          <h3 className="text-sm font-semibold text-brand-text mb-4 flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" />
            Filtros
          </h3>
          {filterContent}
        </div>
      </aside>
    </>
  )
}
